"use strict";

import { useEffect, useState } from "react";

import {
  getMediaPlaybackRefreshDelay,
  hydrateMediaPlaybackUrl,
  PLAYBACK_REFRESH_RETRY_MS,
} from "../utils/audioMedia";
import { getPublicStorageUrl } from "../utils/storageUrls";

const isProjectObjectName = (value, projectId) =>
  typeof value === "string" &&
  value.replace(/^\/+/, "").startsWith(`projects/${projectId}/`);

export const getFallbackPlaybackUrl = (reference, projectId) => {
  if (!reference || isProjectObjectName(reference, projectId)) {
    return "";
  }
  if (/^(https?:|blob:|data:)/i.test(reference)) {
    return reference;
  }
  return getPublicStorageUrl(reference);
};

export const useProjectMediaPlayback = ({ reference, projectId }) => {
  const durableObjectName = isProjectObjectName(reference, projectId)
    ? reference.replace(/^\/+/, "")
    : null;
  const [playbackUrl, setPlaybackUrl] = useState(
    getFallbackPlaybackUrl(reference, projectId)
  );

  useEffect(() => {
    if (!durableObjectName || !projectId) {
      setPlaybackUrl(getFallbackPlaybackUrl(reference, projectId));
      return undefined;
    }

    let cancelled = false;
    let refreshTimer;
    const refresh = async (forceRefresh = false) => {
      const nextPlaybackUrl = await hydrateMediaPlaybackUrl(
        durableObjectName,
        projectId,
        { forceRefresh }
      );
      if (cancelled) {
        return;
      }
      setPlaybackUrl(nextPlaybackUrl);
      const delay = getMediaPlaybackRefreshDelay(durableObjectName, projectId);
      refreshTimer = window.setTimeout(
        () => refresh(true),
        Math.max(delay ?? PLAYBACK_REFRESH_RETRY_MS, PLAYBACK_REFRESH_RETRY_MS)
      );
    };

    refresh();
    return () => {
      cancelled = true;
      window.clearTimeout(refreshTimer);
    };
  }, [durableObjectName, projectId, reference]);

  return {
    objectName: durableObjectName,
    playbackUrl,
    playbackUnavailable: Boolean(durableObjectName && !playbackUrl),
  };
};
